/**
 * MODULE: _shared/frontend-shell/src/features/crm/utils/crmFilterUrl.ts
 * PURPOSE: Map CRM routes to URL query param names and parse or serialize `CrmFilterState`
 * DEPENDENCIES: ../types/crm.types
 * PUBLIC_API: CrmUrlFilterProfile, crmUrlFilterProfileFromPathname, filtersFromSearchParams, applyFiltersToSearchParams, filterRecordFromSearchParams, applySavedFilterRecordToSearchParams, savedViewKeysForProfile
 * SIDE_EFFECTS: None
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 290/300
 * RECENT: 2026-06-22 [2.2.1470] — **`/crm/accounts`** territory profile includes **`scope`** query key for team/all deep links — View Current Accounts sprint.
 * RECENT_PRIOR: 2026-05-08 [2.2.884] **`CrmUrlFilterProfile`** **`tasks`**
 * RECENT_PRIOR: 2026-04-30 [2.2.758] **`filterRecordFromSearchParams`** / **`applySavedFilterRecordToSearchParams`** for CRM saved views — CHANGELOG [2.2.758]. [2.2.748] URL profiles — CHANGELOG [2.2.748]
 */

import type { CrmFilterState } from '../types/crm.types';

export type CrmUrlFilterProfile = 'home' | 'deals' | 'contacts' | 'territory' | 'tasks' | 'pipeline';

/** Query keys persisted for named CRM views (must match `setFilterOnSearchParams` / `applyFiltersToSearchParams`). */
export function savedViewKeysForProfile(profile: CrmUrlFilterProfile): readonly string[] {
  if (profile === 'home') return ['q'];
  if (profile === 'deals') return ['q', 'stage', 'owner', 'territory', 'sort'];
  if (profile === 'contacts') return ['q', 'owner', 'territory', 'tag'];
  if (profile === 'territory') return ['q', 'region', 'scope'];
  if (profile === 'pipeline') return ['q', 'status', 'owner'];
  if (profile === 'tasks') return ['q', 'status', 'due'];
  return ['q'];
}

/** Snapshot active URL filter params as a plain string map for POST /crm/saved-views. */
export function filterRecordFromSearchParams(profile: CrmUrlFilterProfile, sp: URLSearchParams): Record<string, string> {
  const out: Record<string, string> = {};
  for (const k of savedViewKeysForProfile(profile)) {
    const v = sp.get(k);
    if (v != null && v.trim() !== '') out[k] = v.trim();
  }
  return out;
}

/** Build query string params from a saved view payload (unknown keys ignored). */
export function applySavedFilterRecordToSearchParams(
  profile: CrmUrlFilterProfile,
  rec: Record<string, string>
): URLSearchParams {
  const next = new URLSearchParams();
  const allowed = new Set(savedViewKeysForProfile(profile));
  for (const [k, raw] of Object.entries(rec)) {
    if (!allowed.has(k)) continue;
    const v = String(raw ?? '').trim();
    if (v !== '') next.set(k, v);
  }
  return next;
}

export function crmUrlFilterProfileFromPathname(pathname: string): CrmUrlFilterProfile {
  const p = pathname.replace(/\/$/, '') || pathname;
  if (p === '/crm' || p === '') return 'home';
  if (p.startsWith('/crm/deals')) return 'deals';
  if (p.startsWith('/crm/contacts')) return 'contacts';
  if (p.startsWith('/crm/accounts') || p.startsWith('/crm/territory')) return 'territory';
  if (p.startsWith('/crm/tasks')) return 'tasks';
  if (p.startsWith('/crm/pipeline-generation')) return 'pipeline';
  return 'home';
}

function readQ(sp: URLSearchParams): string {
  return sp.get('q') ?? '';
}

/** Build `CrmFilterState` from the current URL for a given profile (empty strings omitted on optional fields). */
export function filtersFromSearchParams(profile: CrmUrlFilterProfile, sp: URLSearchParams): CrmFilterState {
  const search = readQ(sp);
  const base: CrmFilterState = { search };

  if (profile === 'deals') {
    const next: CrmFilterState = { ...base };
    const stage = sp.get('stage')?.trim();
    const owner = sp.get('owner')?.trim();
    const territory = sp.get('territory')?.trim();
    const sort = sp.get('sort')?.trim();
    if (stage) next.stage = stage;
    if (owner) next.owner = owner;
    if (territory) next.territory = territory;
    if (sort) next.sort = sort;
    return next;
  }

  if (profile === 'contacts') {
    const next: CrmFilterState = { ...base };
    const owner = sp.get('owner')?.trim();
    const territory = sp.get('territory')?.trim();
    const tag = sp.get('tag')?.trim();
    if (owner) next.owner = owner;
    if (territory) next.territory = territory;
    if (tag) next.status = tag;
    return next;
  }

  if (profile === 'territory') {
    const next: CrmFilterState = { ...base };
    const region = sp.get('region')?.trim();
    if (region) next.region = region;
    return next;
  }

  if (profile === 'pipeline') {
    const next: CrmFilterState = { ...base };
    const status = sp.get('status')?.trim();
    const owner = sp.get('owner')?.trim();
    if (status) next.status = status;
    if (owner) next.owner = owner;
    return next;
  }

  if (profile === 'tasks') {
    const next: CrmFilterState = { ...base };
    const status = sp.get('status')?.trim();
    const due = sp.get('due')?.trim();
    if (status) next.status = status;
    if (due) next.taskDue = due;
    return next;
  }

  return base;
}

/** Merge one filter key into URLSearchParams for `profile` (caller replaces history). */
export function setFilterOnSearchParams(
  profile: CrmUrlFilterProfile,
  sp: URLSearchParams,
  key: keyof CrmFilterState,
  value: string
): URLSearchParams {
  const next = new URLSearchParams(sp.toString());

  const setQ = (q: string) => {
    if (!q.trim()) next.delete('q');
    else next.set('q', q);
  };

  if (profile === 'home') {
    if (key === 'search') setQ(value);
    return next;
  }

  if (profile === 'deals') {
    if (key === 'search') {
      setQ(value);
      return next;
    }
    if (key === 'stage') {
      if (!value.trim()) next.delete('stage');
      else next.set('stage', value);
      return next;
    }
    if (key === 'owner') {
      if (!value.trim()) next.delete('owner');
      else next.set('owner', value);
      return next;
    }
    if (key === 'territory') {
      if (!value.trim()) next.delete('territory');
      else next.set('territory', value);
      return next;
    }
    if (key === 'sort') {
      if (!value.trim()) next.delete('sort');
      else next.set('sort', value);
      return next;
    }
    if (key === 'dateRange') {
      /* deals profile has no dateRange in URL — ignore */
      return next;
    }
    return next;
  }

  if (profile === 'contacts') {
    if (key === 'search') {
      setQ(value);
      return next;
    }
    if (key === 'owner') {
      if (!value.trim()) next.delete('owner');
      else next.set('owner', value);
      return next;
    }
    if (key === 'territory') {
      if (!value.trim()) next.delete('territory');
      else next.set('territory', value);
      return next;
    }
    if (key === 'status') {
      if (!value.trim()) next.delete('tag');
      else next.set('tag', value);
      return next;
    }
    return next;
  }

  if (profile === 'territory') {
    if (key === 'search') {
      setQ(value);
      return next;
    }
    if (key === 'region') {
      if (!value.trim()) next.delete('region');
      else next.set('region', value);
      return next;
    }
    return next;
  }

  if (profile === 'pipeline') {
    if (key === 'search') {
      setQ(value);
      return next;
    }
    if (key === 'status') {
      if (!value.trim()) next.delete('status');
      else next.set('status', value);
      return next;
    }
    if (key === 'owner') {
      if (!value.trim()) next.delete('owner');
      else next.set('owner', value);
      return next;
    }
    return next;
  }

  if (profile === 'tasks') {
    if (key === 'search') {
      setQ(value);
      return next;
    }
    if (key === 'status') {
      if (!value.trim()) next.delete('status');
      else next.set('status', value);
      return next;
    }
    if (key === 'taskDue') {
      if (!value.trim() || value === 'all') next.delete('due');
      else next.set('due', value);
      return next;
    }
    return next;
  }

  return next;
}

/** Replace URL params for this profile from a full `CrmFilterState` snapshot (used by applyFilters). */
export function applyFiltersToSearchParams(profile: CrmUrlFilterProfile, filters: CrmFilterState): URLSearchParams {
  const next = new URLSearchParams();
  if (profile === 'home') {
    if (filters.search.trim()) next.set('q', filters.search.trim());
    return next;
  }
  if (profile === 'deals') {
    if (filters.search.trim()) next.set('q', filters.search.trim());
    if (filters.stage?.trim()) next.set('stage', filters.stage.trim());
    if (filters.owner?.trim()) next.set('owner', filters.owner.trim());
    if (filters.territory?.trim()) next.set('territory', filters.territory.trim());
    if (filters.sort?.trim()) next.set('sort', filters.sort.trim());
    return next;
  }
  if (profile === 'contacts') {
    if (filters.search.trim()) next.set('q', filters.search.trim());
    if (filters.owner?.trim()) next.set('owner', filters.owner.trim());
    if (filters.territory?.trim()) next.set('territory', filters.territory.trim());
    if (filters.status?.trim()) next.set('tag', filters.status.trim());
    return next;
  }
  if (profile === 'territory') {
    if (filters.search.trim()) next.set('q', filters.search.trim());
    if (filters.region?.trim()) next.set('region', filters.region.trim());
    return next;
  }
  if (profile === 'pipeline') {
    if (filters.search.trim()) next.set('q', filters.search.trim());
    if (filters.status?.trim()) next.set('status', filters.status.trim());
    if (filters.owner?.trim()) next.set('owner', filters.owner.trim());
    return next;
  }
  if (profile === 'tasks') {
    if (filters.search.trim()) next.set('q', filters.search.trim());
    if (filters.status?.trim()) next.set('status', filters.status.trim());
    if (filters.taskDue?.trim() && filters.taskDue !== 'all') next.set('due', filters.taskDue.trim());
    return next;
  }
  return next;
}
